import { useFormatter, useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BaseLineCopy } from '@/app/components/icons/BaseLineCopy';
import { BaseLineFeedback } from '@/app/components/icons/BaseLineFeedback';
import { CollectBtn } from '@/app/components/shared/Community';
import { EllipsisDropdownMenu } from '@/app/components/shared/EllipsisDropdownMenu';
import { ImageApplyButton } from '@/app/components/shared/ImageApplyButton';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import LikeButton from '@/app/components/shared/LikeButton';
import { MediaPromptsDisplay } from '@/app/components/shared/MediaPromptsDisplay';
import { hasTextWithTagsContent, TextWithTags } from '@/app/components/shared/TextWithTags';
import { Button } from '@/app/components/ui/button';
import { Loader } from '@/app/components/ui/loader';
import { SCENE, SITE, TRACE_EVENT_ID, TRACE_EVENT_NAME, TYPE_KEY_MAP } from '@/app/constants';
import { useCopy, useGlobalModal, useModelVersionText, useRecreate } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import {
  type ActionDialogType,
  CommunityEnum,
  EllipsisActionTypeEnum,
  type FormTabType,
  type ModelVersion,
} from '@/app/types';
import { cn, formatAspectRatio, getImageSize, getReportParams, getResourceUrl, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import type { InspirationInterface as ImageInspirationProps } from '../types';
import { getDisplayIpLocation, getTaskInputs } from '../utils';
import { ActivityInfo } from './ActivityInfo';
import { CommunityShareBtn } from './CommunityShareBtn';
import { UserInfo } from './UserInfo';

const ORIGIN_RESOLUTION_ARR = ['512', '720p', '1080p', '2k', '4k'];

export const ImageInspiration = ({
  inspiration,
  handleCollect,
  handleUnCollect,
  onClose,
  onRecreateStart,
  handleVote,
  handleUnVote,
  mediaAreaRef,
}: ImageInspirationProps) => {
  const t = useTranslations();
  const format = useFormatter();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const date = new Date(inspiration.info.created_at ?? '');
  const pathname = usePathname();
  const isActivity = inspiration.isActivity;
  const { open: openActionDialog } = useGlobalModal('actionDialog');
  const { copy } = useCopy();
  const { userInfo } = useStore((state) => ({
    userInfo: state.userInfo,
  }));
  const { handleRecreate } = useRecreate();
  const { getModelVersionText } = useModelVersionText();

  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  useEffect(() => {
    const fetchImageSize = async () => {
      const imageUrl = getResourceUrl({
        uri: inspiration.info.creation.uri,
        noMarkUri: inspiration.info.creation.nomark_uri || '',
      });

      try {
        const { width, height } = await getImageSize(imageUrl);
        setAspectRatio(width / height);
      } catch {
        // 如果获取失败，使用默认的 16:9
        setAspectRatio(16 / 9);
      }
    };

    fetchImageSize();
  }, [inspiration.info.id]);

  const taskInfo = useMemo(() => getTaskInputs(inspiration.task), [inspiration.task]);

  const handleCopyPrompt = async () => {
    if (!taskInfo) return;
    const prompt = taskInfo.textPrompt;
    copy(prompt);
  };

  const handleActionDialog = (action: EllipsisActionTypeEnum) => {
    const reportParams = inspiration.task?.type
      ? getReportParams(inspiration.task?.type, taskInfo?.settings, taskInfo?.templateScene)
      : {};
    openActionDialog({
      action: action as ActionDialogType,
      id: inspiration.info.id,
      type: 'creation',
      reportParams: reportParams,
    });
  };

  /**
   * 媒体区域
   * @returns
   */
  const renderMediaArea = useCallback(() => {
    if (!aspectRatio)
      return (
        <div className="flex flex-1 items-center justify-center">
          <Loader />
        </div>
      );

    const isPortrait = aspectRatio < 1;
    const isSquare = aspectRatio === 1;

    return (
      <div
        ref={mediaAreaRef as React.RefObject<HTMLDivElement | null>}
        className="relative flex flex-1 items-center justify-center p-8 md:flex-none md:p-0"
      >
        <div
          className={cn(
            'relative',
            isPortrait
              ? 'h-full w-auto md:h-[50vh]'
              : isSquare
                ? 'max-h-[60vh] w-full md:max-h-none'
                : 'max-h-full w-full max-w-full',
          )}
          style={{ aspectRatio }}
        >
          <ImageWithPlaceholder
            src={getResourceUrl({
              uri: inspiration.info.creation.uri,
              noMarkUri: inspiration.info.creation.nomark_uri || '',
            })}
            key={`creation_detail_${inspiration.info.id}`}
            alt=""
            className="skip-login-check h-full w-full rounded-12 bg-black object-contain md:rounded-none"
            fullScreen
            loading="eager"
            fetchPriority="high"
          />
        </div>
      </div>
    );
  }, [inspiration.info.id, aspectRatio]);

  /**
   * 图片灵感信息栏
   * @returns
   */
  const renderInfo = () => {
    return (
      <>
        <div className="flex h-full w-[304px] flex-col overflow-y-auto pb-16 md:w-full">
          {inspiration.info.creator ? (
            <UserInfo
              user={inspiration.info.creator}
              ipLocation={getDisplayIpLocation(inspiration.info.creator.id, inspiration.ip_location)}
            />
          ) : userInfo ? (
            <UserInfo user={userInfo} ipLocation={getDisplayIpLocation(userInfo.id, inspiration.ip_location)} />
          ) : null}

          {inspiration.info.brief && (
            <div className="mt-3 break-all text-sm text-white leading-5 md:mt-2">{inspiration.info.brief}</div>
          )}

          <div className="mt-6 md:mt-5">
            <span className="font-semibold text-sm text-system-white">{t('creations.detail.imageDetail')}</span>
          </div>

          <div
            className={cn(
              'mt-5 flex flex-col gap-3 md:mt-4 md:gap-2',
              !hasTextWithTagsContent(taskInfo?.textPrompt, taskInfo?.prompts) && 'flex-initial',
            )}
          >
            {
              // 媒体区域（图片/视频/主体）
              taskInfo?.mediaPrompts && taskInfo.mediaPrompts.length > 0 && (
                <div>
                  <MediaPromptsDisplay prompts={taskInfo.mediaPrompts} taskType={taskInfo.taskType} />
                </div>
              )
            }

            {taskInfo && hasTextWithTagsContent(taskInfo.textPrompt, taskInfo.prompts) && (
              <div>
                <div className="relative w-full text-sm text-system-white leading-5">
                  <TextWithTags
                    text={taskInfo.textPrompt}
                    prompts={taskInfo.prompts}
                    onClick={onClose}
                    tagClassName="bg-white/10 hover:bg-system-white/20"
                  />
                  {taskInfo?.textPrompt && (
                    <Button size="original" variant="text" className="ml-2 translate-y-0.5">
                      <BaseLineCopy
                        className="size-4 text-system-white64 hover:text-system-white"
                        onClick={handleCopyPrompt}
                      />
                    </Button>
                  )}
                </div>
              </div>
            )}

            {renderSettings()}

            {renderExtraInfo()}

            {renderActivityInfo()}
          </div>

          {renderInspirationOperations()}
        </div>

        <div className="mt-4 w-full">{renderCommonOperations()}</div>
      </>
    );
  };

  /**
   * 基本的参数信息
   * @returns
   */
  const renderSettings = () => {
    if (!taskInfo?.settings)
      return (
        <div className="break-all pb-6 text-sm text-white leading-5">{t('create.createForm.settings.noImage')}</div>
      );

    const renderTag = (tag: string) => (
      <span className="h-6 rounded-6 bg-system-white10 px-2 py-1 text-system-white64 text-xs">{tag}</span>
    );

    // 模版只展示模版名称和 aspect_ratio
    if (taskInfo.taskType === SCENE) {
      return (
        <div className="flex flex-wrap items-center gap-1">
          {/* 任务类型 */}
          {renderTag(t(`create.${TYPE_KEY_MAP.scene}`))}
          {/* 模板标签 */}
          {inspiration.info?.tag && renderTag(inspiration.info?.tag)}
          {/* 宽高比 */}
          {taskInfo.settings.aspect_ratio && renderTag(formatAspectRatio(taskInfo.settings.aspect_ratio, t))}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap items-center gap-1">
        {/* 任务类型 */}
        {renderTag(t(`create.${TYPE_KEY_MAP[taskInfo.taskType]}`))}
        {/* 模型版本 */}
        {renderTag(
          getModelVersionText({
            modelVersion: taskInfo.settings.model_version as ModelVersion,
            transition: taskInfo.settings.transition,
          }),
        )}
        {/* 清晰度 */}
        {taskInfo.settings.resolution &&
          ORIGIN_RESOLUTION_ARR.includes(taskInfo.settings.resolution) &&
          renderTag(
            taskInfo.settings.resolution === '2k' || taskInfo.settings.resolution === '4k'
              ? taskInfo.settings.resolution.toUpperCase()
              : t(`create.createForm.settings.resolution.option${taskInfo.settings.resolution as '1080p'}.text`),
          )}
        {/* 宽高比 */}
        {(() => {
          const formattedRatio = formatAspectRatio(taskInfo.settings.aspect_ratio, t, {
            taskType: taskInfo.taskType as Exclude<FormTabType, 'scene'>,
            modelVersion: taskInfo.settings.model_version as ModelVersion,
          });
          return formattedRatio ? renderTag(formattedRatio) : null;
        })()}
      </div>
    );
  };

  /**
   * 图片灵感的额外信息
   * 包括：灵感被使用次数、创建时间、AI 生成水印
   * @returns
   */
  const renderExtraInfo = () => {
    return (
      <div className="flex flex-wrap items-center md:mt-1 [&>span.hidden+span]:before:content-none [&>span:not(:first-child)]:before:mx-2 [&>span:not(:first-child)]:before:inline-block [&>span:not(:first-child)]:before:h-[10px] [&>span:not(:first-child)]:before:w-[1px] [&>span:not(:first-child)]:before:bg-system-white/20 [&>span:not(:first-child)]:before:content-['']">
        <span className="text-system-white/50 text-xs">
          {t('creations.detail.inspired', { count: inspiration.info.recreate_count || 0 })}
        </span>
        {inspiration.info.created_at && (
          <span className="text-system-white/50 text-xs">
            {format.dateTime(date, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              timeZone,
            })}
          </span>
        )}
        {SITE === 'china' && <span className="text-system-white/50 text-xs">内容由 AI 生成</span>}
      </div>
    );
  };

  /**
   * 活动投稿的信息
   * 包括：活动名称、活动状态、奖项
   */
  const renderActivityInfo = () => {
    if (!inspiration.activity) return null;
    return <ActivityInfo activity={inspiration.activity} award={inspiration.info.award} />;
  };

  /**
   * 针对图片灵感的操作按钮
   * 包括：应用图片到、做同款
   * @returns
   */
  const renderInspirationOperations = () => {
    const { type: taskType = 'unspecified', id: taskId = '', input: taskInput, creations } = inspiration.task || {};
    const creation = creations?.[0];

    if (taskType === 'unspecified' || taskType === 'upscale') return null;

    return (
      <div className="mt-6 flex w-full items-center justify-center gap-2 md:mt-5 md:gap-3">
        <ImageApplyButton
          noMarkUri={creation?.nomark_uri}
          uri={creation?.uri}
          inspirationId={inspiration.info.id}
          openInNewTab={true}
          content={
            <div
              className="flex h-10 flex-1 cursor-pointer items-center justify-center rounded-8 bg-white/10 font-normal text-sm hover:bg-system-white/20"
              onClick={() => {
                onClose?.();
              }}
            >
              {t('creation.action.apply')}
            </div>
          }
          onApplyToTool={() => {
            reportEvent(TRACE_EVENT_NAME[CommunityEnum.ImageAsset], {
              [TRACE_EVENT_ID[CommunityEnum.ImageAsset]]: inspiration.info.id,
              interaction_type: 'apply',
              interaction_position: pathname,
              content_type: 'image',
            });
          }}
        />
        <Button
          className="h-10 flex-1 px-0"
          variant="primary"
          size="sm"
          onClick={() => {
            if (!taskInput) {
              return;
            }
            onRecreateStart?.();
            handleRecreate({
              input: taskInput,
              creationId: '',
              inspirationId: inspiration.info.id,
              openNewTab: true,
              settings: taskInfo?.settings || {},
              source: 'inspiration',
              taskId,
              taskType,
              templateScene: taskInfo?.templateScene,
            });
            reportEvent(TRACE_EVENT_NAME[CommunityEnum.ImageAsset], {
              [TRACE_EVENT_ID[CommunityEnum.ImageAsset]]: inspiration.info.id,
              interaction_position: pathname,
              interaction_type: 'recreate',
              trace_id: inspiration.traceId || '',
              content_type: 'image',
            });
          }}
        >
          {t('home.explore.promptButton')}
        </Button>
      </div>
    );
  };

  /**
   * 在信息栏最底部展示的通用操作按钮
   * 包括：收藏、分享、更多、点赞
   * @returns
   */
  const renderCommonOperations = () => {
    const { react_info } = inspiration.info;

    return (
      <div className="flex h-10 items-center gap-2 md:h-12 md:gap-3">
        <CollectBtn
          key={inspiration.info.id}
          id={inspiration.info.id}
          tabType={CommunityEnum.ImageAsset}
          itemCollect={inspiration.info.react_info?.collected ?? false}
          itemCollectCount={inspiration.info.react_info?.collect_count ?? 0}
          handleCollect={handleCollect}
          handleUnCollect={handleUnCollect}
          hideCount={true}
          className={cn(
            'flex aspect-square size-10 h-full cursor-pointer items-center justify-center rounded-10 bg-white/10 text-lg backdrop-blur-lg hover:bg-system-white/20 md:size-12 md:rounded-12',
            isActivity && 'hidden',
          )}
          iconClassName="size-5 md:size-6"
          traceId={inspiration.traceId}
        />

        <CommunityShareBtn id={inspiration.info.id} type={CommunityEnum.ImageAsset} isActivity={isActivity} />
        {SITE === 'china' && (
          <EllipsisDropdownMenu
            className="aspect-square size-10 h-full rounded-10 bg-white/10 text-lg backdrop-blur-lg hover:bg-system-white/20 md:size-12 md:rounded-12 md:text-2xl"
            actions={[{ action: EllipsisActionTypeEnum.InspirationReport, icon: <BaseLineFeedback /> }]}
            onSelectedAction={handleActionDialog}
          />
        )}
        {react_info && (
          <LikeButton
            iconClassName="md:size-6"
            className="h-full flex-1 rounded-10 bg-white/10 text-lg backdrop-blur-lg hover:bg-system-white/20 md:size-12 md:rounded-12"
            id={inspiration.info.id}
            itemVote={react_info?.voted || false}
            tabType={CommunityEnum.ImageAsset}
            itemVoteCount={react_info?.vote_count || 0}
            handleVote={handleVote}
            handleUnVote={handleUnVote}
            traceId={inspiration.traceId}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className="absolute inset-0 z-[-1] bg-center bg-cover bg-no-repeat blur-[150px] md:hidden"
        style={{
          backgroundImage: `url('${inspiration.info.creation.cover_uri}')`,
        }}
      >
        <div className="absolute inset-0 z-[-1] size-full bg-black/60" />
      </div>

      <div className="relative flex size-full flex-1 md:flex-col md:overflow-y-auto md:overflow-x-hidden">
        {renderMediaArea()}
        {/* 右侧信息栏 */}
        <div className="relative my-8 flex flex-col border-system-white10 border-l pr-[30px] pl-5 md:my-0 md:flex-1 md:border-0 md:px-4 md:pb-6">
          {renderInfo()}
        </div>
      </div>
    </>
  );
};
